import { devicesAPI } from '../../api/index'

const state = {
  devices: [],
  currentDevice: null,
  deviceSensors: [],
  deviceStatus: null,
  realTimeData: null,
  loading: false,
  error: null,
  pagination: {
    total: 0,
    per_page: 15,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0
  }
}

const mutations = {
  SET_DEVICES(state, devices) {
    state.devices = devices
  },
  SET_CURRENT_DEVICE(state, device) {
    state.currentDevice = device
  },
  SET_DEVICE_SENSORS(state, sensors) {
    state.deviceSensors = sensors
  },
  SET_DEVICE_STATUS(state, status) {
    state.deviceStatus = status
  },
  SET_REAL_TIME_DATA(state, data) {
    state.realTimeData = data
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  CLEAR_ERROR(state) {
    state.error = null
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = pagination
  },
  UPDATE_DEVICE(state, updatedDevice) {
    const index = state.devices.findIndex(device => device.id === updatedDevice.id)
    if (index !== -1) {
      state.devices.splice(index, 1, updatedDevice)
    }
  },
  ADD_DEVICE(state, device) {
    state.devices.unshift(device)
  },
  REMOVE_DEVICE(state, deviceId) {
    state.devices = state.devices.filter(device => device.id !== deviceId)
  }
}

const actions = {
  async fetchDevices({ commit }, params = {}) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await devicesAPI.getAll(params)
      
      // La nueva estructura tiene data.data que contiene la paginación y data.data.data que contiene los dispositivos
      const { data: paginatedData, ...pagination } = response.data.data
      const devices = paginatedData.data || paginatedData // Fallback para compatibilidad
      
      commit('SET_DEVICES', devices)
      commit('SET_PAGINATION', pagination)
      
      return { success: true, data: devices, pagination }
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'Error al cargar dispositivos'
      commit('SET_ERROR', message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchDeviceById({ commit }, deviceId) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await devicesAPI.getById(deviceId)
      // La nueva estructura tiene data.data que contiene el dispositivo
      const device = response.data.data.data || response.data.data
      commit('SET_CURRENT_DEVICE', device)
      
      return device
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'Error al cargar dispositivo'
      commit('SET_ERROR', message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchDeviceSensors({ commit }, deviceId) {
    try {
      commit('CLEAR_ERROR')
      
      const response = await devicesAPI.getSensors(deviceId)
      commit('SET_DEVICE_SENSORS', response.data.data)
      
      return response.data.data
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'Error al cargar sensores del dispositivo'
      commit('SET_ERROR', message)
      throw error
    }
  },

  async fetchDeviceStatus({ commit }, deviceId) {
    try {
      commit('CLEAR_ERROR')
      
      const response = await devicesAPI.getStatus(deviceId)
      commit('SET_DEVICE_STATUS', response.data.data)
      
      return response.data.data
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'Error al cargar estado del dispositivo'
      commit('SET_ERROR', message)
      throw error
    }
  },

  async fetchRealTimeData({ commit }, deviceId) {
    try {
      commit('CLEAR_ERROR')
      
      const response = await devicesAPI.getRealTimeData(deviceId)
      commit('SET_REAL_TIME_DATA', response.data.data)
      
      return response.data.data
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'Error al cargar datos en tiempo real'
      commit('SET_ERROR', message)
      throw error
    }
  },

  async createDevice({ commit }, deviceData) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await devicesAPI.create(deviceData)
      const newDevice = response.data.data
      
      commit('ADD_DEVICE', newDevice)
      
      return { success: true, device: newDevice }
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'Error al crear dispositivo'
      commit('SET_ERROR', message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateDevice({ commit }, { deviceId, deviceData }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await devicesAPI.update(deviceId, deviceData)
      const updatedDevice = response.data.data
      
      commit('UPDATE_DEVICE', updatedDevice)
      
      return { success: true, device: updatedDevice }
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'Error al actualizar dispositivo'
      commit('SET_ERROR', message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteDevice({ commit }, deviceId) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      await devicesAPI.delete(deviceId)
      commit('REMOVE_DEVICE', deviceId)
      
      return { success: true }
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'Error al eliminar dispositivo'
      commit('SET_ERROR', message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async enableDevice({ commit }, deviceId) {
    try {
      commit('CLEAR_ERROR')
      
      const response = await devicesAPI.enable(deviceId)
      const updatedDevice = response.data.data
      
      commit('UPDATE_DEVICE', updatedDevice)
      
      return { success: true, device: updatedDevice }
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'Error al habilitar dispositivo'
      commit('SET_ERROR', message)
      throw error
    }
  },

  async disableDevice({ commit }, deviceId) {
    try {
      commit('CLEAR_ERROR')
      
      const response = await devicesAPI.disable(deviceId)
      const updatedDevice = response.data.data
      
      commit('UPDATE_DEVICE', updatedDevice)
      
      return { success: true, device: updatedDevice }
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'Error al deshabilitar dispositivo'
      commit('SET_ERROR', message)
      throw error
    }
  },

  async syncDevice({ commit }, deviceId) {
    try {
      commit('CLEAR_ERROR')
      
      const response = await devicesAPI.sync(deviceId)
      const updatedDevice = response.data.data
      
      commit('UPDATE_DEVICE', updatedDevice)
      
      return { success: true, device: updatedDevice }
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message) || 'Error al sincronizar dispositivo'
      commit('SET_ERROR', message)
      throw error
    }
  },

  clearCurrentDevice({ commit }) {
    commit('SET_CURRENT_DEVICE', null)
    commit('SET_DEVICE_SENSORS', [])
    commit('SET_DEVICE_STATUS', null)
    commit('SET_REAL_TIME_DATA', null)
  }
}

const getters = {
  devices: state => state.devices,
  currentDevice: state => state.currentDevice,
  deviceSensors: state => state.deviceSensors,
  deviceStatus: state => state.deviceStatus,
  realTimeData: state => state.realTimeData,
  loading: state => state.loading,
  error: state => state.error,
  pagination: state => state.pagination,
  
  // Computed getters
  enabledDevices: state => state.devices.filter(device => device.enabled),
  disabledDevices: state => state.devices.filter(device => !device.enabled),
  devicesByProvider: state => providerId => state.devices.filter(device => device.iotProviderId === providerId),
  
  // Device status helpers
  isDeviceOnline: state => deviceId => {
    const device = state.devices.find(d => d.id === deviceId)
    return device && device.last_sync_at && new Date(device.last_sync_at) > new Date(Date.now() - 5 * 60 * 1000)
  },
  
  deviceLastSync: state => deviceId => {
    const device = state.devices.find(d => d.id === deviceId)
    return device ? device.last_sync_at : null
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 


// WEBPACK FOOTER //
// ./src/store/modules/devices.js